import React, { useEffect, useRef, useState } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import Logo from "../images/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Burger from "../components/burger.js";
import Search from "./search";
import Menu from "./menu";
import Image from "./image";
import { HiddenOnDesktop, HiddenOnPhone } from "./hidden.js";

const WidthWrapper = styled.div`
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-flow: row wrap;
  height: auto;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const InnerWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $menuPosition, $logoPosition }) => {
    if ($menuPosition === "center" && $logoPosition === "center") {
      return "center";
    } else if ($menuPosition === "center" && $logoPosition !== "center") {
      return "flex-start";
    } else if ($logoPosition === "center" && $menuPosition !== "center") {
      return "flex-end";
    } else {
      return "space-between";
    }
  }};
  flex-direction: ${({ $menuPosition, $logoPosition }) =>
    $menuPosition === "center" && $logoPosition === "center"
      ? "column"
      : "row"};
  min-height: 92px;

  @media (max-width: 1024px) {
    justify-content: space-between;
    padding: 15px 0 !important;
    flex-direction: row;
    border-top: 1px solid ${({ theme }) => theme.colors.primary};
  }
`;

const MenuWrapper = styled.div`
  position: ${({ $centered }) => ($centered ? "absolute" : "static")};
  left: ${({ $centered }) => ($centered ? "50%" : "auto")};
  transform: ${({ $centered }) => ($centered ? "translateX(-50%)" : "none")};
  display: flex;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-end")};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    position: static;
    transform: none;
    justify-content: flex-end;
    width: 100%;
    flex-direction: column;
  }
`;

const HeaderWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  color: ${({ theme, $headerContentTransparency, $transparent }) =>
    $headerContentTransparency && $transparent
      ? theme.colors.transparentHeaderTextColor
      : theme.colors.headerLink || theme.colors.black};
  background-color: ${({
    theme,
    $headerContentTransparency,
    $transparent,
    $isFrontPage,
  }) =>
    $headerContentTransparency && $transparent && $isFrontPage
      ? "transparent"
      : theme.colors.headerBackground || theme.colors.secondary};

  transition: background-color 0.3s ease-in-out;
  height: auto;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  box-shadow: ${({ $headerContentTransparency, $transparent, $isFrontPage }) =>
    !$isFrontPage || (!$headerContentTransparency && $transparent)
      ? "0px 6px 10px rgba(0, 0, 0, 0.1)"
      : "transparent"};
  z-index: 999;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  }
`;

const LogoImage = styled(Image)`
  object-fit: contain;
  width: ${({ $logoWidth }) =>
    $logoWidth ? `${$logoWidth}px!important` : "150px"};
  height: ${({ $logoHeight }) =>
    $logoHeight ? `${$logoHeight}px!important` : "50px"};

  @media (max-width: 1920px) {
    margin: ${({ $centered }) => ($centered ? "0" : "0 0 0 2rem")};
  }

  @media (max-width: 1920px) {
    margin-left: ${({ $centered }) => ($centered ? "0" : "2rem")};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: ${({ $logoWidthMobile, $logoWidth }) =>
      $logoWidthMobile
        ? `${$logoWidthMobile}px!important`
        : $logoWidth
          ? `${$logoWidth}px!important`
          : "150px"};
    height: ${({ $logoHeightMobile }) =>
      $logoHeightMobile ? `${$logoHeightMobile}px!important` : "50px"};
  }
`;
const LogoWrapper = styled.div`
  position: ${({ $centered, $menuCentered }) =>
    $centered && !$menuCentered ? "absolute" : "static"};
  left: ${({ $centered }) => ($centered ? "50%" : "auto")};
  transform: ${({ $centered }) => ($centered ? "translateX(-50%)" : "none")};
  z-index: 10;
  display: flex;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  align-items: center;
  padding-top: ${({ $centered, $menuCentered }) =>
    $centered && $menuCentered ? "10px" : "0"};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    position: absolute;
    padding-top: 0;
  }
`;

const StaticLogo = styled.img`
  height: ${({ $logoHeight }) =>
    $logoHeight ? `${$logoHeight}px!important` : "50px"};
  object-fit: contain;
  width: ${({ $logoWidth }) =>
    $logoWidth ? `${$logoWidth}px!important` : "150px"};
  @media (max-width: 1920px) {
    padding-left: ${({ $centered }) => ($centered ? "0" : "2rem")};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    max-width: ${({ $logoWidthMobile }) =>
      $logoWidthMobile ? `${$logoWidthMobile}px!important` : "60%"};
    height: ${({ $logoHeightMobile }) =>
      $logoHeightMobile ? `${$logoHeightMobile}px!important` : "50px"};
  }
`;

const SvgLogo = styled.img`
  width: ${({ $logoWidth }) =>
    $logoWidth ? `${$logoWidth}px!important` : "150px"};
  height: ${({ $logoHeight }) =>
    $logoHeight ? `${$logoHeight}px!important` : "50px"};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: ${({ $centered }) => ($centered ? "0" : "1rem")};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    max-width: ${({ $logoWidthMobile }) =>
      $logoWidthMobile ? `${$logoWidthMobile}px!important` : "60%"};
    height: ${({ $logoHeightMobile }) =>
      $logoHeightMobile ? `${$logoHeightMobile}px!important` : "50px"};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 75px;
  font-size: 1.3rem;
  padding: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    right: 65px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
`;

const Header = ({
  staticQueryData,
  staticSearchData,
  children,
  showUsp,
  isFrontPage,
}) => {
  const [open, setOpen] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [headerContentTransparency, setHeaderContentTransparency] =
    useState(true);

  const { wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      query HeaderQuery {
        wp {
          crbThemeOptions {
            crbLogoHeaderHeight
            crbLogoMobileHeaderHeight
            crbLogoHeaderPosition {
              value
            }
            crbMenuPosition {
              value
            }
            crbLogoMobileHeaderWidth
            crbLogoHeaderWidth
            crbLogoHeaderHeight
            crbLogoMobileHeaderHeight
            crbLogoHeader {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 50
                      layout: FIXED
                      placeholder: NONE
                    )
                  }
                }
              }
            }
            crbLogoMobileHeader {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbSearchbarEnabled
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbTransparentHeader
          }
        }
      }
    `);

  const onScroll = () => {
    if (wp?.crbThemeOptions?.crbTransparentHeader) {
      if (window.scrollY > 50) {
        setHeaderContentTransparency(false);
      } else {
        setHeaderContentTransparency(true);
      }
    }
  };

  React.useEffect(() => {
    if (wp?.crbThemeOptions?.crbTransparentHeader) {
      setHeaderContentTransparency(window.scrollY <= 50);
      window.addEventListener("scroll", onScroll);
    }

    return () => window.removeEventListener("scroll", onScroll);
  }, [wp]);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      const baseHeight = headerRef.current.offsetHeight;
      const uspOffset = showUsp ? 40 : 0;
      setHeaderHeight(baseHeight - uspOffset);
    }

    const handleResize = () => {
      if (headerRef.current) {
        const baseHeight = headerRef.current.offsetHeight;
        const uspOffset = showUsp ? 40 : 0;
        setHeaderHeight(baseHeight - uspOffset);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showUsp]);
  return (
    <>
      <HeaderWrapper
        ref={headerRef}
        $burgerOpen={burgerOpen}
        $headerContentTransparency={headerContentTransparency}
        $transparent={wp?.crbThemeOptions?.crbTransparentHeader}
        $isFrontPage={isFrontPage}
      >
        {children}
        <WidthWrapper>
          <InnerWrapper
            $menuPosition={wp?.crbThemeOptions?.crbMenuPosition?.value}
            $logoPosition={wp?.crbThemeOptions?.crbLogoHeaderPosition?.value}
            style={{
              paddingTop: `${wp?.crbThemeOptions?.crbPaddingTop}px`,
              paddingRight: `${wp?.crbThemeOptions?.crbPaddingRight}px`,
              paddingBottom: `${wp?.crbThemeOptions?.crbPaddingBottom}px`,
              paddingLeft: `${wp?.crbThemeOptions?.crbPaddingLeft}px`,
            }}
          >
            <HiddenOnPhone>
              <LogoWrapper
                $centered={
                  wp?.crbThemeOptions?.crbLogoHeaderPosition?.value === "center"
                }
                $menuCentered={
                  wp?.crbThemeOptions?.crbMenuPosition?.value === "center"
                }
              >
                <Link to="/">
                  {wp.crbThemeOptions.crbLogoHeader?.media?.mimeType ===
                  "image/svg+xml" ? (
                    <SvgLogo
                      src={
                        wp.crbThemeOptions.crbLogoHeader?.media?.localFile
                          ?.publicURL
                      }
                      className="svg-size-header"
                      $logoWidth={wp?.crbThemeOptions?.crbLogoHeaderWidth}
                      $logoHeight={wp?.crbThemeOptions?.crbLogoHeaderHeight}
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  ) : wp?.crbThemeOptions?.crbLogoHeader?.media ? (
                    <LogoImage
                      className="header-logo"
                      alt="Logotype"
                      image={wp?.crbThemeOptions?.crbLogoHeader?.media}
                      loading="eager"
                      $logoWidth={wp?.crbThemeOptions?.crbLogoHeaderWidth}
                      $logoHeight={wp?.crbThemeOptions?.crbLogoHeaderHeight}
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  ) : (
                    <StaticLogo
                      alt="Logotype"
                      src={Logo}
                      $logoWidth={wp?.crbThemeOptions?.crbLogoHeaderWidth}
                      $logoHeight={wp?.crbThemeOptions?.crbLogoHeaderHeight}
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  )}
                </Link>
              </LogoWrapper>
            </HiddenOnPhone>
            <HiddenOnDesktop>
              <LogoWrapper
                $centered={
                  wp?.crbThemeOptions?.crbLogoHeaderPosition?.value === "center"
                }
                $menuCentered={
                  wp?.crbThemeOptions?.crbMenuPosition?.value === "center"
                }
              >
                <Link to="/">
                  {wp.crbThemeOptions.crbLogoMobileHeader?.media?.mimeType ===
                  "image/svg+xml" ? (
                    <SvgLogo
                      src={
                        wp.crbThemeOptions.crbLogoMobileHeader?.media?.localFile
                          ?.publicURL
                      }
                      className="svg-size-header"
                      $logoWidthMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderWidth
                      }
                      $logoHeightMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderHeight
                      }
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  ) : wp?.crbThemeOptions?.crbLogoMobileHeader?.media ? (
                    <LogoImage
                      alt="Logotype"
                      image={wp?.crbThemeOptions?.crbLogoMobileHeader?.media}
                      loading="eager"
                      $logoWidthMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderWidth
                      }
                      $logoHeightMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderHeight
                      }
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  ) : (
                    <StaticLogo
                      alt="Logotype"
                      src={Logo}
                      $logoWidthMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderWidth
                      }
                      $logoHeightMobile={
                        wp?.crbThemeOptions?.crbLogoMobileHeaderHeight
                      }
                      $centered={
                        wp?.crbThemeOptions?.crbLogoHeaderPosition?.value ===
                        "center"
                      }
                      objectFit="contain"
                    />
                  )}
                </Link>
              </LogoWrapper>
            </HiddenOnDesktop>
            <MenuWrapper
              $centered={
                wp?.crbThemeOptions?.crbMenuPosition?.value === "center" &&
                wp?.crbThemeOptions?.crbLogoHeaderPosition?.value !== "center"
              }
            >
              {wp?.crbThemeOptions?.crbSearchbarEnabled && (
                <Search
                  searchOpen={searchOpen}
                  onClose={(state) => setSearchOpen(state)}
                  staticSearchData={staticSearchData}
                />
              )}
              <Menu
                menuPosition={wp?.crbThemeOptions?.crbMenuPosition?.value}
                open={open}
                setOpen={setOpen}
                staticQueryData={staticQueryData}
                burgerOpen={burgerOpen}
                isFrontPage={isFrontPage}
                transparentHeader={
                  wp?.crbThemeOptions?.crbTransparentHeader &&
                  headerContentTransparency
                }
                headerHeight={headerHeight}
              />
            </MenuWrapper>
            {wp?.crbThemeOptions?.crbSearchbarEnabled && (
              <SearchIcon onClick={() => setSearchOpen(true)}>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
            )}
            <Burger
              burgerOpen={burgerOpen}
              setBurgerOpen={setBurgerOpen}
              transparentHeader={
                wp?.crbThemeOptions?.crbTransparentHeader &&
                headerContentTransparency
              }
            />
          </InnerWrapper>
        </WidthWrapper>
      </HeaderWrapper>
      {!wp?.crbThemeOptions?.crbTransparentHeader && (
        <div style={{ height: `${headerHeight}px` }}></div>
      )}
    </>
  );
};

Header.propTypes = {
  staticQueryData: PropTypes.object,
  children: PropTypes.node,
  dropdownOpen: PropTypes.bool,
  staticSearchData: PropTypes.object,
  showUsp: PropTypes.bool,
  isFrontPage: PropTypes.bool,
};

export default Header;
